import { cn } from '@/lib/utils'
import type {
  AlertComparatorCode, AlertMetricCode, BeneficiaryEventTypeCode, BeneficiaryStatusCode,
  DeliveryChannelCode, DisbursementEntryTypeCode, DropReasonCode,
  EcosystemNodeTypeCode, ImpactVerdictCode, KpiDirectionCode, KpiSourceCode,
  MilestonePriorityCode, MilestoneStatusCode, PartnerTypeCode, ProgramCategoryCode,
  ProgramDocumentTypeCode, ProgramExportStatusCode, ProgramStatusCode, ReversalReasonCode,
  ScopingFamilyCode,
} from '@/shared/api/types'

/**
 * ONE vocabulary module for the whole feature — every label, every tone, every
 * ordering. PollSphere defined its programme vocabularies in four places and
 * they had already drifted: "Bursary & Education" in one file and "Education
 * Support" in another, `amber` for PAUSED in one and `slate` in the next.
 *
 * ## Colour never carries meaning alone
 *
 * Every chip renders its label. Colour is reinforcement, so the screens stay
 * readable in greyscale, in print, and to the ~8% of men with a red-green
 * deficiency. An unrecognised code falls through to neutral rather than
 * throwing: a newer backend must render, not crash.
 *
 * ## Both themes, always
 *
 * Every tone is declared for light and dark. PollSphere had no `dark:` variant
 * anywhere in its programme module — each badge was a light pastel that turns
 * into a glowing block on a dark surface.
 */

const CHIP = 'inline-flex items-center gap-1.5 px-2 py-0.5 text-erp-xs font-medium border rounded-md whitespace-nowrap'

type Tone = 'neutral' | 'positive' | 'caution' | 'serious' | 'critical' | 'info' | 'accent' | 'special'

const TONE: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground border-border',
  positive: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
  caution: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
  serious: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-900',
  critical: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900',
  info: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900',
  accent: 'bg-chocolate-soft/60 text-chocolate border-chocolate-muted',
  special: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-900',
}

const TONE_DOT: Record<Tone, string> = {
  neutral: 'bg-slate-400', positive: 'bg-emerald-500', caution: 'bg-amber-500',
  serious: 'bg-orange-500', critical: 'bg-red-500', info: 'bg-sky-500',
  accent: 'bg-chocolate', special: 'bg-violet-500',
}

function Chip({ tone, label, dot, title }: { tone: Tone; label: string; dot?: boolean; title?: string }) {
  return (
    // `data-tone` is a test hook: a dark-mode check needs to find a chip by what
    // it MEANS, not by matching its text against a regex that also matches prose.
    <span className={cn(CHIP, TONE[tone])} title={title} data-tone={tone}>
      {dot && <span className={cn('erp-status-dot', TONE_DOT[tone])} />}
      {label}
    </span>
  )
}

function meta<T extends string>(map: Record<string, { label: string; tone: Tone }>, code?: string | null) {
  return map[(code ?? 'UNKNOWN') as T] ?? { label: code ?? '—', tone: 'neutral' as Tone }
}

/** Fill for a 0–100 meter. Same three thresholds everywhere in the feature. */
export function meterTone(pct: number): string {
  if (pct >= 70) return 'bg-emerald-500'
  if (pct >= 40) return 'bg-amber-500'
  return 'bg-red-500'
}

// ── programme status ────────────────────────────────────────────────────────

const PROGRAM_STATUS: Record<string, { label: string; tone: Tone }> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  ACTIVE: { label: 'Active', tone: 'positive' },
  PAUSED: { label: 'Paused', tone: 'caution' },
  COMPLETED: { label: 'Completed', tone: 'info' },
  CANCELLED: { label: 'Cancelled', tone: 'critical' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}

export const PROGRAM_STATUS_ORDER: ProgramStatusCode[] =
  ['DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELLED']

export const programStatusLabel = (c?: string | null) => meta(PROGRAM_STATUS, c).label

export function ProgramStatusBadge({ code }: { code?: string | null }) {
  const m = meta<ProgramStatusCode>(PROGRAM_STATUS, code)
  return <Chip tone={m.tone} label={m.label} dot />
}

/**
 * The transitions the server allows. The UI offers exactly these, so a user
 * never sees an option that returns a 409 naming both states.
 *
 *   DRAFT     → ACTIVE | CANCELLED       (ACTIVE additionally needs a published rule)
 *   ACTIVE    → PAUSED | COMPLETED | CANCELLED
 *   PAUSED    → ACTIVE | COMPLETED | CANCELLED
 *   COMPLETED → terminal
 *   CANCELLED → terminal
 */
export const STATUS_TRANSITIONS: Record<string, ProgramStatusCode[]> = {
  DRAFT: ['ACTIVE', 'CANCELLED'],
  ACTIVE: ['PAUSED', 'COMPLETED', 'CANCELLED'],
  PAUSED: ['ACTIVE', 'COMPLETED', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
  UNKNOWN: [],
}

// ── category & channel ──────────────────────────────────────────────────────

const CATEGORY: Record<string, { label: string; tone: Tone }> = {
  BURSARY: { label: 'Bursary', tone: 'info' },
  HEALTH: { label: 'Health', tone: 'critical' },
  WATER: { label: 'Water', tone: 'info' },
  AGRICULTURE: { label: 'Agriculture', tone: 'positive' },
  YOUTH_EMPOWERMENT: { label: 'Youth empowerment', tone: 'accent' },
  WOMEN_EMPOWERMENT: { label: 'Women empowerment', tone: 'special' },
  INFRASTRUCTURE: { label: 'Infrastructure', tone: 'neutral' },
  RELIEF: { label: 'Relief', tone: 'serious' },
  TRAINING: { label: 'Training', tone: 'caution' },
  OTHER: { label: 'Other', tone: 'neutral' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}

export const CATEGORY_ORDER: ProgramCategoryCode[] = [
  'BURSARY', 'HEALTH', 'WATER', 'AGRICULTURE', 'YOUTH_EMPOWERMENT',
  'WOMEN_EMPOWERMENT', 'INFRASTRUCTURE', 'RELIEF', 'TRAINING', 'OTHER',
]

export const categoryLabel = (c?: string | null) => meta(CATEGORY, c).label
export function CategoryBadge({ code }: { code?: string | null }) {
  const m = meta<ProgramCategoryCode>(CATEGORY, code)
  return <Chip tone={m.tone} label={m.label} />
}

const CHANNEL: Record<string, { label: string; tone: Tone }> = {
  DIRECT_CASH: { label: 'Direct cash', tone: 'caution' },
  MOBILE_MONEY: { label: 'Mobile money', tone: 'positive' },
  IN_KIND: { label: 'In kind', tone: 'info' },
  VOUCHER: { label: 'Voucher', tone: 'accent' },
  SERVICE: { label: 'Service', tone: 'special' },
  EVENT: { label: 'Event', tone: 'neutral' },
  INSTITUTIONAL: { label: 'Institutional', tone: 'neutral' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}

export const CHANNEL_ORDER: DeliveryChannelCode[] = [
  'DIRECT_CASH', 'MOBILE_MONEY', 'IN_KIND', 'VOUCHER', 'SERVICE', 'EVENT', 'INSTITUTIONAL',
]
export const channelLabel = (c?: string | null) => meta(CHANNEL, c).label
export function ChannelBadge({ code }: { code?: string | null }) {
  const m = meta<DeliveryChannelCode>(CHANNEL, code)
  return <Chip tone={m.tone} label={m.label} />
}

// ── beneficiary lifecycle ───────────────────────────────────────────────────

const BENEFICIARY_STATUS: Record<string, { label: string; tone: Tone }> = {
  SHORTLISTED: { label: 'Shortlisted', tone: 'neutral' },
  ENROLLED: { label: 'Enrolled', tone: 'info' },
  DELIVERED: { label: 'Delivered', tone: 'positive' },
  COMPLETED: { label: 'Completed', tone: 'accent' },
  DROPPED: { label: 'Dropped', tone: 'critical' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}

export const BENEFICIARY_STATUS_ORDER: BeneficiaryStatusCode[] =
  ['SHORTLISTED', 'ENROLLED', 'DELIVERED', 'COMPLETED', 'DROPPED']

export const beneficiaryStatusLabel = (c?: string | null) => meta(BENEFICIARY_STATUS, c).label
export function BeneficiaryStatusBadge({ code }: { code?: string | null }) {
  const m = meta<BeneficiaryStatusCode>(BENEFICIARY_STATUS, code)
  return <Chip tone={m.tone} label={m.label} dot />
}

/**
 * What the server accepts. `DELIVERED → DELIVERED` is legal and deliberate — a
 * programme that trucks water weekly delivers to the same person forty times,
 * and only the first stamps `firstDeliveredAt`.
 */
export const BENEFICIARY_TRANSITIONS: Record<string, BeneficiaryStatusCode[]> = {
  SHORTLISTED: ['ENROLLED', 'DROPPED'],
  ENROLLED: ['DELIVERED', 'COMPLETED', 'DROPPED'],
  DELIVERED: ['DELIVERED', 'COMPLETED', 'DROPPED'],
  COMPLETED: ['DROPPED'],
  DROPPED: ['SHORTLISTED', 'ENROLLED'],
  UNKNOWN: [],
}

const EVENT_TYPE: Record<string, { label: string; tone: Tone }> = {
  SHORTLISTED: { label: 'Shortlisted', tone: 'neutral' },
  ENROLLED: { label: 'Enrolled', tone: 'info' },
  DELIVERY_RECORDED: { label: 'Delivery recorded', tone: 'positive' },
  COMPLETED: { label: 'Completed', tone: 'accent' },
  DROPPED: { label: 'Dropped', tone: 'critical' },
  REINSTATED: { label: 'Reinstated', tone: 'caution' },
  DETAILS_UPDATED: { label: 'Details updated', tone: 'neutral' },
  VOTER_LINKED: { label: 'Linked to register', tone: 'special' },
  VOTER_UNLINKED: { label: 'Unlinked from register', tone: 'serious' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const eventTypeLabel = (c?: string | null) => meta(EVENT_TYPE, c).label
export function EventTypeBadge({ code }: { code?: string | null }) {
  const m = meta<BeneficiaryEventTypeCode>(EVENT_TYPE, code)
  return <Chip tone={m.tone} label={m.label} />
}

const DROP_REASON: Record<string, { label: string; tone: Tone }> = {
  INELIGIBLE: { label: 'Ineligible', tone: 'neutral' },
  DUPLICATE: { label: 'Duplicate', tone: 'caution' },
  DECLINED: { label: 'Declined', tone: 'neutral' },
  UNREACHABLE: { label: 'Unreachable', tone: 'caution' },
  DECEASED: { label: 'Deceased', tone: 'neutral' },
  MOVED_AWAY: { label: 'Moved away', tone: 'neutral' },
  FRAUD_SUSPECTED: { label: 'Fraud suspected', tone: 'critical' },
  PROGRAMME_CANCELLED: { label: 'Programme cancelled', tone: 'serious' },
  OTHER: { label: 'Other', tone: 'neutral' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const DROP_REASON_ORDER: DropReasonCode[] = [
  'INELIGIBLE', 'DUPLICATE', 'DECLINED', 'UNREACHABLE', 'DECEASED', 'MOVED_AWAY',
  'FRAUD_SUSPECTED', 'PROGRAMME_CANCELLED', 'OTHER',
]
export const dropReasonLabel = (c?: string | null) => meta(DROP_REASON, c).label
export function DropReasonBadge({ code }: { code?: string | null }) {
  const m = meta<DropReasonCode>(DROP_REASON, code)
  return <Chip tone={m.tone} label={m.label} />
}

// ── milestones & KPIs ───────────────────────────────────────────────────────

const MILESTONE_STATUS: Record<string, { label: string; tone: Tone }> = {
  PENDING: { label: 'Pending', tone: 'neutral' },
  IN_PROGRESS: { label: 'In progress', tone: 'info' },
  COMPLETED: { label: 'Completed', tone: 'positive' },
  BLOCKED: { label: 'Blocked', tone: 'critical' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const MILESTONE_STATUS_ORDER: MilestoneStatusCode[] =
  ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'BLOCKED', 'CANCELLED']
export const milestoneStatusLabel = (c?: string | null) => meta(MILESTONE_STATUS, c).label
export function MilestoneStatusBadge({ code }: { code?: string | null }) {
  const m = meta<MilestoneStatusCode>(MILESTONE_STATUS, code)
  return <Chip tone={m.tone} label={m.label} dot />
}

const PRIORITY: Record<string, { label: string; tone: Tone }> = {
  LOW: { label: 'Low', tone: 'neutral' },
  MEDIUM: { label: 'Medium', tone: 'info' },
  HIGH: { label: 'High', tone: 'serious' },
  CRITICAL: { label: 'Critical', tone: 'critical' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const PRIORITY_ORDER: MilestonePriorityCode[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']
export const priorityLabel = (c?: string | null) => meta(PRIORITY, c).label
export function PriorityBadge({ code }: { code?: string | null }) {
  const m = meta<MilestonePriorityCode>(PRIORITY, code)
  return <Chip tone={m.tone} label={m.label} />
}

/**
 * The honesty column, and the most important chip in the module.
 *
 * A `MANUAL` KPI is a number somebody typed into a form. PollSphere let a user
 * type a "current value" and then charted it as an achievement. Every screen
 * that shows a KPI shows where its number came from.
 */
const KPI_SOURCE: Record<string, { label: string; tone: Tone }> = {
  MANUAL: { label: 'Reported', tone: 'caution' },
  DERIVED_ENROLMENT: { label: 'Derived — enrolment', tone: 'positive' },
  DERIVED_DELIVERY: { label: 'Derived — delivery', tone: 'positive' },
  DERIVED_SPEND: { label: 'Derived — spend', tone: 'positive' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const KPI_SOURCE_ORDER: KpiSourceCode[] =
  ['MANUAL', 'DERIVED_ENROLMENT', 'DERIVED_DELIVERY', 'DERIVED_SPEND']
export const kpiSourceLabel = (c?: string | null) => meta(KPI_SOURCE, c).label
export function KpiSourceBadge({ code }: { code?: string | null }) {
  const m = meta<KpiSourceCode>(KPI_SOURCE, code)
  return (
    <Chip
      tone={m.tone}
      label={m.label}
      title={code === 'MANUAL'
        ? 'A number somebody typed. Not measured by the system.'
        : 'Computed from this feature’s own tables.'}
    />
  )
}

const KPI_DIRECTION: Record<string, { label: string; tone: Tone }> = {
  HIGHER_IS_BETTER: { label: 'Higher is better', tone: 'neutral' },
  LOWER_IS_BETTER: { label: 'Lower is better', tone: 'neutral' },
  TARGET_IS_BEST: { label: 'On target is best', tone: 'neutral' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const KPI_DIRECTION_ORDER: KpiDirectionCode[] =
  ['HIGHER_IS_BETTER', 'LOWER_IS_BETTER', 'TARGET_IS_BEST']
export const kpiDirectionLabel = (c?: string | null) => meta(KPI_DIRECTION, c).label

// ── documents & partners ────────────────────────────────────────────────────

const DOC_TYPE: Record<string, { label: string; tone: Tone }> = {
  PROPOSAL: { label: 'Proposal', tone: 'info' },
  BUDGET: { label: 'Budget', tone: 'accent' },
  AGREEMENT: { label: 'Agreement', tone: 'special' },
  REPORT: { label: 'Report', tone: 'neutral' },
  PHOTO: { label: 'Photo', tone: 'neutral' },
  RECEIPT: { label: 'Receipt', tone: 'caution' },
  ATTENDANCE: { label: 'Attendance', tone: 'positive' },
  OTHER: { label: 'Other', tone: 'neutral' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const DOC_TYPE_ORDER: ProgramDocumentTypeCode[] =
  ['PROPOSAL', 'BUDGET', 'AGREEMENT', 'REPORT', 'PHOTO', 'RECEIPT', 'ATTENDANCE', 'OTHER']
export const docTypeLabel = (c?: string | null) => meta(DOC_TYPE, c).label
export function DocTypeBadge({ code }: { code?: string | null }) {
  const m = meta<ProgramDocumentTypeCode>(DOC_TYPE, code)
  return <Chip tone={m.tone} label={m.label} />
}

const PARTNER_TYPE: Record<string, { label: string; tone: Tone }> = {
  GOVERNMENT: { label: 'Government', tone: 'info' },
  NGO: { label: 'NGO', tone: 'special' },
  CBO: { label: 'CBO', tone: 'positive' },
  FAITH_BASED: { label: 'Faith-based', tone: 'accent' },
  PRIVATE_SECTOR: { label: 'Private sector', tone: 'caution' },
  INDIVIDUAL: { label: 'Individual', tone: 'neutral' },
  OTHER: { label: 'Other', tone: 'neutral' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const PARTNER_TYPE_ORDER: PartnerTypeCode[] =
  ['GOVERNMENT', 'NGO', 'CBO', 'FAITH_BASED', 'PRIVATE_SECTOR', 'INDIVIDUAL', 'OTHER']
export const partnerTypeLabel = (c?: string | null) => meta(PARTNER_TYPE, c).label
export function PartnerTypeBadge({ code }: { code?: string | null }) {
  const m = meta<PartnerTypeCode>(PARTNER_TYPE, code)
  return <Chip tone={m.tone} label={m.label} />
}

// ── money ───────────────────────────────────────────────────────────────────

const ENTRY_TYPE: Record<string, { label: string; tone: Tone }> = {
  DISBURSEMENT: { label: 'Disbursement', tone: 'positive' },
  REVERSAL: { label: 'Reversal', tone: 'critical' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const entryTypeLabel = (c?: string | null) => meta(ENTRY_TYPE, c).label
export function EntryTypeBadge({ code }: { code?: string | null }) {
  const m = meta<DisbursementEntryTypeCode>(ENTRY_TYPE, code)
  return <Chip tone={m.tone} label={m.label} />
}

const REVERSAL_REASON: Record<string, { label: string; tone: Tone }> = {
  DATA_ENTRY_ERROR: { label: 'Data entry error', tone: 'neutral' },
  DUPLICATE_PAYMENT: { label: 'Duplicate payment', tone: 'caution' },
  RECIPIENT_INELIGIBLE: { label: 'Recipient ineligible', tone: 'caution' },
  FRAUD_CONFIRMED: { label: 'Fraud confirmed', tone: 'critical' },
  RETURNED_BY_RECIPIENT: { label: 'Returned by recipient', tone: 'info' },
  PROGRAMME_CANCELLED: { label: 'Programme cancelled', tone: 'serious' },
  OTHER: { label: 'Other', tone: 'neutral' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const REVERSAL_REASON_ORDER: ReversalReasonCode[] = [
  'DATA_ENTRY_ERROR', 'DUPLICATE_PAYMENT', 'RECIPIENT_INELIGIBLE', 'FRAUD_CONFIRMED',
  'RETURNED_BY_RECIPIENT', 'PROGRAMME_CANCELLED', 'OTHER',
]
export const reversalReasonLabel = (c?: string | null) => meta(REVERSAL_REASON, c).label

// ── impact ──────────────────────────────────────────────────────────────────

/**
 * The verdict is a claim about what CAN be said, not about whether a programme
 * worked. `MEASURED` is the only one that licenses a causal reading, and the
 * other four each name a missing input.
 */
const VERDICT: Record<string, { label: string; tone: Tone }> = {
  MEASURED: { label: 'Measured', tone: 'positive' },
  NO_CONTROL: { label: 'No control group', tone: 'caution' },
  INSUFFICIENT_DATA: { label: 'Too few readings', tone: 'caution' },
  TOO_SOON: { label: 'Too soon', tone: 'neutral' },
  NOT_MEASURABLE: { label: 'Not measurable', tone: 'neutral' },
}
export const verdictLabel = (c?: string | null) => meta(VERDICT, c).label
export function VerdictBadge({ code }: { code?: string | null }) {
  const m = meta<ImpactVerdictCode>(VERDICT, code)
  return <Chip tone={m.tone} label={m.label} dot />
}

// ── analytics ───────────────────────────────────────────────────────────────

/**
 * Which question a screen answers, rendered on every analytics screen because
 * the two families give DIFFERENT answers on purpose: a ward coordinator sees a
 * national programme's whole totals under PROGRAMME and only their own ward's
 * rows under DELIVERY. Both are right, and a reader who does not know which one
 * they are looking at will put them in the same sentence.
 */
const SCOPING: Record<string, { label: string; tone: Tone; hint: string }> = {
  PROGRAMME: {
    label: 'Programme scope', tone: 'info',
    hint: 'Programmes you can see, counted whole — including national programmes.',
  },
  DELIVERY: {
    label: 'Delivery scope', tone: 'accent',
    hint: 'Work recorded in your area only. A person with no recorded unit appears to national users only.',
  },
}
export function ScopingBadge({ code }: { code?: ScopingFamilyCode | null }) {
  // A missing scoping family is NOT defaulted to PROGRAMME. The badge exists
  // because confusing the two families is the failure mode it guards against,
  // and asserting one of them when the server sent neither is that same error
  // wearing a chip.
  if (!code) {
    return (
      <Chip
        tone="caution"
        label="Scope unknown"
        title="This response did not say which question it answers, so whether the totals are whole programmes or only your own units cannot be stated."
      />
    )
  }
  const m = SCOPING[code]
  if (!m) return null
  return <Chip tone={m.tone} label={m.label} title={m.hint} />
}

const NODE_TYPE: Record<string, { label: string; tone: Tone }> = {
  PROGRAMME: { label: 'Programme', tone: 'accent' },
  MOBILIZER: { label: 'Mobilizer', tone: 'info' },
  PARTNER: { label: 'Partner', tone: 'special' },
  CHANNEL: { label: 'Channel', tone: 'caution' },
  UNIT: { label: 'Unit', tone: 'positive' },
  CANDIDATE: { label: 'Candidate', tone: 'neutral' },
  PARTY: { label: 'Party', tone: 'neutral' },
}
export const nodeTypeLabel = (c?: string | null) => meta(NODE_TYPE, c).label
export function NodeTypeBadge({ code }: { code?: string | null }) {
  const m = meta<EcosystemNodeTypeCode>(NODE_TYPE, code)
  return <Chip tone={m.tone} label={m.label} />
}

/**
 * Node colours for the graph. The BACKEND returns no colours — it sends
 * `nodeTypeCode` and the client owns the palette, because PollSphere hardcoded
 * `#1c1917` and it is invisible in dark mode. These are CSS variables so both
 * themes are one definition.
 */
export const NODE_FILL: Record<string, string> = {
  PROGRAMME: 'var(--chart-1)',
  MOBILIZER: 'var(--chart-2)',
  PARTNER: 'var(--chart-3)',
  CHANNEL: 'var(--chart-4)',
  UNIT: 'var(--chart-5)',
  CANDIDATE: 'var(--muted-foreground)',
  PARTY: 'var(--muted-foreground)',
}

const EDGE_TYPE: Record<string, { label: string; tone: Tone }> = {
  EXECUTES: { label: 'Executes', tone: 'info' },
  FUNDS: { label: 'Funds', tone: 'accent' },
  DELIVERS_THROUGH: { label: 'Delivers through', tone: 'caution' },
  OPERATES_IN: { label: 'Operates in', tone: 'positive' },
  SHARES_BENEFICIARIES: { label: 'Shares beneficiaries', tone: 'special' },
}
export const edgeTypeLabel = (c?: string | null) => meta<string>(EDGE_TYPE, c).label

const ALERT_METRIC: Record<string, { label: string; tone: Tone }> = {
  COVERAGE: { label: 'Coverage', tone: 'info' },
  SPEND_PCT_OF_BUDGET: { label: 'Spend % of budget', tone: 'caution' },
  BURN_RATE: { label: 'Burn rate', tone: 'caution' },
  DAYS_SINCE_ENROLMENT: { label: 'Days since an enrolment', tone: 'neutral' },
  DROP_RATE: { label: 'Drop rate', tone: 'serious' },
  UPLIFT: { label: 'Measured uplift', tone: 'positive' },
  DUPLICATE_COUNT: { label: 'Cross-programme duplicates', tone: 'serious' },
  RECON_DISCREPANCY: { label: 'Reconciliation discrepancy', tone: 'critical' },
  MILESTONES_OVERDUE: { label: 'Overdue milestones', tone: 'caution' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const ALERT_METRIC_ORDER: AlertMetricCode[] = [
  'COVERAGE', 'SPEND_PCT_OF_BUDGET', 'BURN_RATE', 'DAYS_SINCE_ENROLMENT', 'DROP_RATE',
  'UPLIFT', 'DUPLICATE_COUNT', 'RECON_DISCREPANCY', 'MILESTONES_OVERDUE',
]
export const alertMetricLabel = (c?: string | null) => meta(ALERT_METRIC, c).label
export function AlertMetricBadge({ code }: { code?: string | null }) {
  const m = meta<AlertMetricCode>(ALERT_METRIC, code)
  return <Chip tone={m.tone} label={m.label} />
}

/** Two metrics need a window; the rest ignore one and the form must not offer it. */
export const METRIC_NEEDS_WINDOW: AlertMetricCode[] = ['BURN_RATE', 'DAYS_SINCE_ENROLMENT']

/** Metrics whose threshold is a fraction the user types as a percentage. */
export const METRIC_IS_PERCENT: AlertMetricCode[] = ['COVERAGE', 'DROP_RATE', 'SPEND_PCT_OF_BUDGET']

export const comparatorLabel = (c?: AlertComparatorCode | null) =>
  c === 'BELOW' ? 'falls below' : c === 'ABOVE' ? 'rises above' : '—'

const EXPORT_STATUS: Record<string, { label: string; tone: Tone }> = {
  QUEUED: { label: 'Queued', tone: 'neutral' },
  RUNNING: { label: 'Running', tone: 'info' },
  COMPLETED: { label: 'Ready', tone: 'positive' },
  FAILED: { label: 'Failed', tone: 'critical' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}
export const exportStatusLabel = (c?: string | null) => meta(EXPORT_STATUS, c).label
export function ExportStatusBadge({ code }: { code?: string | null }) {
  const m = meta<ProgramExportStatusCode>(EXPORT_STATUS, code)
  return <Chip tone={m.tone} label={m.label} />
}

// ── the segment DSL, for the rule builder ───────────────────────────────────
//
// LABELS ONLY. The fields, the kinds, the operators and the allowed values all
// come from `GET /programs/targeting/fields` at runtime — there is deliberately
// no second copy of the whitelist in this codebase. A field the backend adds
// tomorrow renders with a humanised fallback rather than not rendering at all.

const FIELD_LABEL: Record<string, string> = {
  supportBand: 'Support band',
  supportLevel: 'Support score',
  supportConfidence: 'Support confidence',
  ageBand: 'Age band',
  birthYear: 'Year of birth',
  gender: 'Gender',
  registrationStatus: 'Registration status',
  verificationStatus: 'Verification status',
  status: 'Register status',
  preferredLanguage: 'Preferred language',
  literacy: 'Literacy',
  householdRole: 'Household role',
  householdId: 'Household',
  countyId: 'County',
  constituencyId: 'Constituency',
  wardId: 'Ward (registration)',
  residenceWardId: 'Ward (residence)',
  pollingStationId: 'Polling station',
  assignedMobilizerId: 'Assigned mobilizer',
  doNotContact: 'Do not contact',
  tag: 'Tag',
}

export const fieldLabel = (f: string) =>
  FIELD_LABEL[f] ?? f.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())

const OPERATOR_LABEL: Record<string, string> = {
  eq: 'is', ne: 'is not', in: 'is any of',
  gte: 'is at least', lte: 'is at most', gt: 'is more than', lt: 'is less than',
  between: 'is between',
}
export const operatorLabel = (op: string) => OPERATOR_LABEL[op] ?? op

const VALUE_LABEL: Record<string, string> = {
  A18_24: '18–24', A25_34: '25–34', A35_44: '35–44',
  A45_54: '45–54', A55_64: '55–64', A65_PLUS: '65+',
  MALE: 'Male', FEMALE: 'Female', OTHER: 'Other', UNDISCLOSED: 'Undisclosed',
  OPPOSED: 'Opposed', LEAN_OPPOSED: 'Leaning opposed', UNDECIDED: 'Undecided',
  LEAN_SUPPORT: 'Leaning support', SUPPORTER: 'Supporter', UNASSESSED: 'Never assessed',
  REGISTERED: 'Registered', UNREGISTERED: 'Not registered', PENDING: 'Pending',
  TRANSFERRED_OUT: 'Transferred out', DECEASED: 'Deceased', INELIGIBLE: 'Ineligible',
  SUSPENDED: 'Suspended',
  LITERATE: 'Literate', SEMI_LITERATE: 'Semi-literate', ILLITERATE: 'Illiterate',
  UNRECORDED: 'Not recorded',
  HEAD: 'Head', SPOUSE: 'Spouse', CHILD: 'Child', PARENT: 'Parent',
  RELATIVE: 'Relative', TENANT: 'Tenant',
  INFERRED: 'Inferred', REPORTED: 'Reported', VERIFIED: 'Verified',
}
export const valueLabel = (v: string) => VALUE_LABEL[v] ?? v
